// A room's door (docs/claude/rooms.md, *Doors*): everything a view asks of one
// door, from ONE place — what it is built as (its kind: a plain leaf, a PT10
// glazing, a PT9 single door), the wall run it sits on, its jambs, its swing,
// the band its opening cuts out of the wall, its top. The plan, the sections,
// 3D, the DXF, the hit tests, the handles and the cards read this record instead
// of each working the door out again. Pure and DOM-free (test/roomDoor.test.mjs).
import type {
  ProductInstance,
  SaunaDoor,
  Vec2,
} from '../shared/types';
import { wallThicknessOf } from './dent';
import { type DoorKind } from './door';
import { type DoorSwing } from './doorSwing';
import { arcPoints } from './geometry';
import { doorTop } from './heights';
import {
  type DoorRun,
  doorRun,
  doorSpanOn,
  innerAt,
  innerWallSpanBand,
  outerAt,
  polyEdges,
} from './polyRoom';
import {
  glazingOfDoor,
  type RoomGlazing,
} from './roomGlazing';

/** One door of a room, in the room's local frame. */
export interface RoomDoor {
  door: SaunaDoor;
  /** On a partition (else on an outer wall). */
  onPartition: boolean;
  /** The wall run it sits on, and its opening along it. */
  run: DoorRun;
  span: { start: number; end: number };
  /** Its jambs: at the span's start and end, on the face it is drawn on. */
  jambs: { start: Vec2; end: Vec2 };
  /** Its leaf open square to the wall, and the arc back to the far jamb. */
  swing: DoorSwing;
  /** The band its opening cuts through its wall or partition; null for none. */
  gap: Vec2[] | null;
  /** Its leaf's top, mm above the room's floor. */
  top: number;
  /** What it is built as (`doorKindRule`, decided where glazings are found). */
  readonly kind: DoorKind;
  /** The glazing it is the door of (a PT10 or a PT9), or null for a plain leaf. */
  readonly glazing: RoomGlazing | null;
}

/** The door's record, or null when it has no run (its partition gone). `door`
 *  need not be in the room yet (the Door tool's preview). */
export function roomDoor(room: ProductInstance, door: SaunaDoor): RoomDoor | null {
  const run = doorRun(room, door);
  if (!run) return null;
  const span = doorSpanOn(run, door);
  const at = (d: number): Vec2 => ({ x: run.base.x + run.along.x * d, y: run.base.y + run.along.y * d });
  const jambs = { start: at(span.start), end: at(span.end) };
  const hingeAtStart = door.hinge === 'left';
  const hinge = hingeAtStart ? jambs.start : jambs.end;
  const swing: DoorSwing = {
    hinge,
    open: { x: hinge.x + run.swing.x * door.width, y: hinge.y + run.swing.y * door.width },
    closed: hingeAtStart ? jambs.end : jambs.start,
    radius: door.width,
  };
  const onPartition = door.innerWallId != null;
  let glazing: RoomGlazing | null | undefined;
  const glazingNow = (): RoomGlazing | null => (glazing === undefined ? (glazing = glazingOfDoor(room, door.id)) : glazing);
  return {
    door, onPartition, run, span, jambs, swing,
    gap: gapOf(room, door, span),
    top: doorTop(room, door),
    get glazing() { return glazingNow(); },
    get kind() { return glazingNow()?.system ?? 'plain'; },
  };
}

/** A door's record by id among the room's doors. */
export function roomDoorById(room: ProductInstance, id: string | null | undefined): RoomDoor | null {
  const d = id ? (room.doors ?? []).find((x) => x.id === id) : undefined;
  return d ? roomDoor(room, d) : null;
}

/** The band a door's opening cuts: an outer door's between its jambs from the
 *  outer face to the inner one; a partition door's across the partition. */
function gapOf(room: ProductInstance, door: SaunaDoor, span: { start: number; end: number }): Vec2[] | null {
  if (door.innerWallId != null) {
    const iw = (room.innerWalls ?? []).find((w) => w.id === door.innerWallId);
    return iw ? innerWallSpanBand(room, iw, span.start, span.end) : null;
  }
  const e = polyEdges(room)[door.edge ?? 0];
  if (!e) return null;
  const w = wallThicknessOf(room);
  return [outerAt(e, span.start), outerAt(e, span.end), innerAt(e, span.end, w), innerAt(e, span.start, w)];
}

/** What "the door" covers to a highlight or a zoom-to: the opening and the
 *  quarter its leaf sweeps (the swing is most of what is seen of it). */
export function doorFootprint(rd: RoomDoor): Vec2[] {
  const { hinge, open, closed, radius } = rd.swing;
  const arc = arcPoints(hinge, open, closed, radius);
  return [hinge, ...(arc.length >= 2 ? arc : [open, closed])];
}

/** Whether a local point is on the door: between its jambs (± `margin`), from
 *  the wall's face out over its swing. */
export function doorHit(rd: RoomDoor, p: Vec2, margin: number): boolean {
  const rel = { x: p.x - rd.run.base.x, y: p.y - rd.run.base.y };
  const along = rel.x * rd.run.along.x + rel.y * rd.run.along.y;
  const out = rel.x * rd.run.swing.x + rel.y * rd.run.swing.y;
  return along >= rd.span.start - margin && along <= rd.span.end + margin && out >= -margin && out <= rd.door.width + margin;
}

/** A local point's station along the door's run (clamped to it): a move or a
 *  jamb's resize. */
export function doorAlong(rd: RoomDoor, p: Vec2): number {
  const d = (p.x - rd.run.base.x) * rd.run.along.x + (p.y - rd.run.base.y) * rd.run.along.y;
  return Math.max(0, Math.min(rd.run.len, d));
}

/** Where the selected door's icons sit: `flip` (the hinge's side) just inside the
 *  room on its centreline, opposite the swing; `swing` (push / pull, a
 *  partition's door only) across the partition from it. */
export function doorIcons(room: ProductInstance, rd: RoomDoor): { flip: Vec2; swing: Vec2 | null } {
  const mid = (rd.span.start + rd.span.end) / 2;
  const c = { x: rd.run.base.x + rd.run.along.x * mid, y: rd.run.base.y + rd.run.along.y * mid };
  const inset = Math.max(60, Math.min(220, wallThicknessOf(room) + 120));
  const off = (k: number): Vec2 => ({ x: c.x + rd.run.swing.x * inset * k, y: c.y + rd.run.swing.y * inset * k });
  return { flip: off(-1), swing: rd.onPartition ? off(1) : null };
}

/** The narrowest a door is resized to by a jamb's drag (mm). */
export const DOOR_MIN_WIDTH = 300;

/** Resize the door by moving one jamb to station `along` on its run, the other
 *  jamb kept: `'left'` is the run's start, `'right'` its end; never narrower than
 *  `DOOR_MIN_WIDTH`, never past the run's ends. */
export function resizeDoorTo(rd: RoomDoor, side: 'left' | 'right', along: number): void {
  const { door, run } = rd;
  const mid = run.len / 2 + door.offset;
  let start = mid - door.width / 2, end = mid + door.width / 2;
  if (side === 'right') end = Math.max(start + DOOR_MIN_WIDTH, Math.min(run.len, along));
  else start = Math.min(end - DOOR_MIN_WIDTH, Math.max(0, along));
  door.width = end - start;
  door.offset = (start + end) / 2 - run.len / 2;
}
